import React, { useState, useEffect, useRef } from 'react';
import { Modal, Form, Input, InputNumber, message, Switch, DatePicker, Divider, Select, Upload, Avatar, Alert, Button, Spin, Popconfirm } from 'antd';
import { UploadOutlined, ShopOutlined, UserOutlined, SwapOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../services/api';
import partnerService from '../services/partnerService';
import CityAutocomplete from './CityAutocomplete';

const { TextArea } = Input;
const { Option } = Select;

// VAT number validation (same as backend)
const validateTaxNumber = (_, value) => {
  if (!value) {
    return Promise.reject('Please enter tax number');
  }

  const cleanValue = value.trim().toUpperCase().replace(/[\s-]/g, '');

  // Greek VAT (AFM): exactly 9 digits
  const greekPattern = /^\d{9}$/;
  // EU VAT: 2-letter country code + digits (7-12 digits)
  const euPattern = /^[A-Z]{2}\d{7,12}$/;
  // Greek VAT with EL prefix
  const greekWithPrefixPattern = /^EL\d{9}$/;

  if (greekPattern.test(cleanValue) || euPattern.test(cleanValue) || greekWithPrefixPattern.test(cleanValue)) {
    return Promise.resolve();
  }

  return Promise.reject('Invalid VAT format. Greek: 9 digits. EU: 2-letter code + digits (e.g., EL123456789)');
};

const EditPartnerModal = ({ visible, partner, onClose, onUpdate }) => {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);

  // User change state
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [selectedNewUser, setSelectedNewUser] = useState(null);
  const [changingUser, setChangingUser] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (partner && visible) {
      form.setFieldsValue({
        store_name: partner.store_name,
        tax_number: partner.tax_number,
        address: partner.address,
        city: partner.city,
        postal_code: partner.postal_code,
        country: partner.country,
        platform_fee_percentage: partner.platform_fee_percentage,
        is_verified: partner.is_verified,
        collaboration_start_date: partner.collaboration_start_date ? dayjs(partner.collaboration_start_date) : null,
        collaboration_end_date: partner.collaboration_end_date ? dayjs(partner.collaboration_end_date) : null,
        contract_renewal_date: partner.contract_renewal_date ? dayjs(partner.contract_renewal_date) : null,
        kyc_status: partner.kyc_status || 'PENDING',
        kyc_notes: partner.kyc_notes || '',
      });
      setLogoPreview(partner.logo?.thumbnail || null);
      setLogoFile(null);
      // Reset user change state
      setUserSearchResults([]);
      setSelectedNewUser(null);
    }
  }, [partner, visible, form]);

  const handleLogoChange = (info) => {
    const file = info.file;
    if (file) {
      setLogoFile(file);
      // Create preview URL
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('Image must be smaller than 2MB!');
      return false;
    }
    handleLogoChange({ file });
    return false; // Prevent auto upload
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      // Use FormData if there's a logo file
      let submitData;
      if (logoFile) {
        submitData = new FormData();
        submitData.append('logo', logoFile);
        Object.keys(values).forEach(key => {
          if (key === 'collaboration_start_date' || key === 'collaboration_end_date' || key === 'contract_renewal_date') {
            const dateValue = values[key]?.format('YYYY-MM-DD') || '';
            if (dateValue) {
              submitData.append(key, dateValue);
            }
          } else if (values[key] !== undefined && values[key] !== null) {
            submitData.append(key, values[key]);
          }
        });
      } else {
        // Regular JSON data without logo
        submitData = {
          ...values,
          collaboration_start_date: values.collaboration_start_date?.format('YYYY-MM-DD') || null,
          collaboration_end_date: values.collaboration_end_date?.format('YYYY-MM-DD') || null,
          contract_renewal_date: values.contract_renewal_date?.format('YYYY-MM-DD') || null,
        };
      }

      await onUpdate(partner.id, submitData, !!logoFile);
      message.success('Partner updated successfully!');
      onClose();
    } catch (error) {
      if (!error.errorFields) {
        message.error('Failed to update partner');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setLogoFile(null);
    setLogoPreview(null);
    setUserSearchResults([]);
    setSelectedNewUser(null);
    onClose();
  };

  // Search for client users
  const handleUserSearch = async (searchValue) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (!searchValue || searchValue.length < 2) {
      setUserSearchResults([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(async () => {
      setSearchingUsers(true);
      try {
        const response = await api.get('/accounts/admin/users/', {
          params: { role: 'CLIENT', search: searchValue }
        });
        const users = response.data.results || response.data || [];
        // Filter out users that are already partners or team members
        const availableUsers = users.filter(user =>
          !user.partner_profile && !user.partner_team_membership
        );
        setUserSearchResults(availableUsers);
      } catch (error) {
        console.error('Error searching users:', error);
        message.error('Failed to search users');
      } finally {
        setSearchingUsers(false);
      }
    }, 300);
  };

  const handleChangeUser = async () => {
    if (!selectedNewUser) {
      message.error('Please select a user first');
      return;
    }

    setChangingUser(true);
    const result = await partnerService.adminChangePartnerUser(partner.id, selectedNewUser);
    setChangingUser(false);

    if (result.success) {
      message.success('Partner user changed successfully!');
      setSelectedNewUser(null);
      setUserSearchResults([]);
      // Refresh the partner data
      if (onUpdate) {
        onUpdate(partner.id, {}, false, true); // Pass flag to indicate refresh needed
      }
      onClose();
    } else {
      message.error(result.error || 'Failed to change partner user');
    }
  };

  return (
    <Modal
      title="Edit Partner Profile"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      okText="Update"
      cancelText="Cancel"
      confirmLoading={submitting}
      width={600}
      maskClosable={false}
    >
      {partner && (
        <div style={{ marginBottom: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
          <div><strong>Partner ID:</strong> {partner.id}</div>
          <div><strong>Current Email:</strong> {partner.user?.email}</div>
        </div>
      )}

      {/* Change User Section */}
      <Divider>Change Partner User</Divider>
      <Alert
        message="Transfer Ownership"
        description="Search for a client user to transfer this partner account to. Only client users (not partners or admins) can be selected."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <Select
          showSearch
          placeholder="Search user by email..."
          style={{ flex: 1 }}
          filterOption={false}
          onSearch={handleUserSearch}
          onChange={(value) => setSelectedNewUser(value)}
          value={selectedNewUser}
          notFoundContent={searchingUsers ? <Spin size="small" /> : 'No users found'}
          allowClear
          onClear={() => setSelectedNewUser(null)}
        >
          {userSearchResults.map(user => (
            <Option key={user.id} value={user.id}>
              <div>
                <UserOutlined style={{ marginRight: 8 }} />
                {user.email}
                {user.first_name && ` (${user.first_name} ${user.last_name})`}
              </div>
            </Option>
          ))}
        </Select>
        <Popconfirm
          title="Change Partner User"
          description="Are you sure you want to transfer this partner account to the selected user? The current user will become a regular client."
          onConfirm={handleChangeUser}
          okText="Yes, Transfer"
          cancelText="Cancel"
          disabled={!selectedNewUser}
        >
          <Button
            type="primary"
            icon={<SwapOutlined />}
            loading={changingUser}
            disabled={!selectedNewUser}
          >
            Change User
          </Button>
        </Popconfirm>
      </div>

      <Divider>Partner Information</Divider>

      <Form
        form={form}
        layout="vertical"
      >
        {/* Logo Upload */}
        <Form.Item label="Partner Logo">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {logoPreview ? (
              <Avatar
                size={80}
                src={logoPreview}
                style={{ border: '2px solid #f0f0f0' }}
              />
            ) : (
              <Avatar
                size={80}
                icon={<ShopOutlined />}
                style={{ backgroundColor: '#ff7a45' }}
              />
            )}
            <Upload
              beforeUpload={beforeUpload}
              showUploadList={false}
              accept="image/*"
            >
              <button type="button" className="ant-btn ant-btn-default">
                <UploadOutlined /> {logoPreview ? 'Change Logo' : 'Upload Logo'}
              </button>
            </Upload>
          </div>
        </Form.Item>

        <Form.Item
          label="Store/Business Name"
          name="store_name"
          rules={[{ required: true, message: 'Please enter store name' }]}
        >
          <Input placeholder="Enter store or business name" />
        </Form.Item>

        <Form.Item
          label="Tax Number (VAT)"
          name="tax_number"
          rules={[{ validator: validateTaxNumber }]}
          extra="Greek: 9 digits | EU: Country code + digits (e.g., EL123456789)"
        >
          <Input placeholder="Enter tax number" />
        </Form.Item>

        <Form.Item
          label="Platform Fee (%)"
          name="platform_fee_percentage"
          rules={[
            { required: true, message: 'Please enter platform fee' },
            { type: 'number', min: 0, max: 100, message: 'Fee must be between 0 and 100' }
          ]}
        >
          <InputNumber
            min={0}
            max={100}
            style={{ width: '100%' }}
            placeholder="Enter platform fee percentage"
          />
        </Form.Item>

        <Form.Item
          label="Address"
          name="address"
          rules={[{ required: true, message: 'Please enter address' }]}
        >
          <TextArea
            rows={2}
            placeholder="Enter full street address"
          />
        </Form.Item>

        <Form.Item
          label="City"
          name="city"
          rules={[{ required: true, message: 'Please enter city' }]}
        >
          <CityAutocomplete placeholder="Enter city" />
        </Form.Item>

        <Form.Item
          label="Postal Code"
          name="postal_code"
        >
          <Input placeholder="Enter postal code (optional)" />
        </Form.Item>

        <Form.Item
          label="Country"
          name="country"
          rules={[{ required: true, message: 'Please enter country' }]}
        >
          <Input placeholder="Enter country" />
        </Form.Item>

        <Form.Item
          label="Verified Status"
          name="is_verified"
          valuePropName="checked"
        >
          <Switch checkedChildren="Verified" unCheckedChildren="Not Verified" />
        </Form.Item>

        <Divider>Collaboration Dates</Divider>

        <Form.Item
          label="Collaboration Start Date"
          name="collaboration_start_date"
        >
          <DatePicker style={{ width: '100%' }} placeholder="Select start date" />
        </Form.Item>

        <Form.Item
          label="Collaboration End Date"
          name="collaboration_end_date"
        >
          <DatePicker style={{ width: '100%' }} placeholder="Select end date (leave empty for ongoing)" />
        </Form.Item>

        <Form.Item
          label="Contract Renewal Date"
          name="contract_renewal_date"
        >
          <DatePicker style={{ width: '100%' }} placeholder="Select renewal date" />
        </Form.Item>

        <Divider>KYC Documentation</Divider>

        <Form.Item
          label="KYC Status"
          name="kyc_status"
        >
          <Select placeholder="Select KYC status">
            <Option value="PENDING">Pending</Option>
            <Option value="RECEIVED">Documents Received</Option>
            <Option value="VERIFIED">Verified</Option>
            <Option value="REJECTED">Rejected</Option>
          </Select>
        </Form.Item>

        <Form.Item
          label="KYC Notes"
          name="kyc_notes"
        >
          <TextArea
            rows={2}
            placeholder="Notes about KYC documentation..."
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default EditPartnerModal;
